"use client";

import { useQueryClient } from "@tanstack/react-query";

import { useTenant } from "@/contexts/tenant-provider";

import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";

import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import type { NoteDetail, TelehealthBotLifecycleStatus } from "@/types/notes";

import { noteQueryKeys } from "@/api/provider/scribe/note-query";

/**
 * Live-patches the bot embedded in the `NoteDetail` cache from the
 * ``TELEHEALTH_BOT_STATUS`` WS subscription.
 *
 * Mirrors the prior `useTelehealthBot` behavior: writes the new
 * `recallBotStatus` directly into the note's cached `telehealthBot`, and
 * invalidates the note detail on terminal statuses so `recallBotEndedAt` and
 * the reconciled transcript get pulled from the server.
 *
 * Mounted once globally inside `RecordingPiP` alongside
 * `useTelehealthRecordingSync` so the transitions still fire when the provider
 * has navigated away from the playground.
 */
export function useTelehealthBotLiveUpdates(note: NoteDetail | undefined) {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();
  const hasRecallModule = tenant?.enabledModules.includes("RECALL") ?? false;
  const eventId = note?.eventId;
  const noteId = note?.id;
  const enabled = !!eventId && hasRecallModule;

  function handleMessage(message: AlertDetail) {
    if (!eventId || !noteId) return;
    if (message.type !== AlertType.TELEHEALTH_BOT_STATUS) return;

    const payload = message.data as {
      eventId?: string;
      noteId?: string | null;
      status?: TelehealthBotLifecycleStatus;
    };
    if (payload?.eventId !== eventId) return;
    // One event can carry multiple bot rows over its lifetime (scheduled +
    // ad-hoc, rejoin-after-ended), so route by noteId, not just eventId —
    // without this, a late terminal event for the prior session's note would
    // patch the current rejoin note's cache. The backend contract is that
    // every published alert carries `note_id` (the Recall webhook skips the
    // publish entirely when it's null), so a missing noteId here means the
    // payload is malformed and we should drop it.
    if (payload.noteId !== noteId) return;
    const nextStatus = payload?.status;
    if (!nextStatus) return;

    queryClient.setQueryData<NoteDetail | undefined>(
      noteQueryKeys.detail(noteId),
      (prev) =>
        prev && prev.telehealthBot
          ? {
              ...prev,
              telehealthBot: {
                ...prev.telehealthBot,
                recallBotStatus: nextStatus,
              },
            }
          : prev,
    );
    // Terminal status messages carry the finalize path's writes — refetch so
    // `recallBotEndedAt` and the reconciled transcript pick up. `transcribed` /
    // `transcribe_failed` are what unblock Generate, so the refresh that pulls
    // the authoritative transcript must run on them too.
    if (
      nextStatus === "ended" ||
      nextStatus === "transcribed" ||
      nextStatus === "transcribe_failed" ||
      nextStatus === "failed"
    ) {
      void queryClient.invalidateQueries({
        queryKey: noteQueryKeys.detail(noteId),
      });
    }
  }

  useWebSocketCallbackSubscription(
    [AlertType.TELEHEALTH_BOT_STATUS],
    handleMessage,
    enabled,
    "TelehealthBotLiveUpdates",
  );
}
